/**
 * Guard for staged work that a screen would otherwise throw away silently.
 *
 * `beforeunload` guards leaving the *page*. A screen that swaps sections in
 * place — Admin Setup's left nav, Admin Settings' tab strip — unmounts the open
 * section without the browser ever being asked, so staged edits vanish with no
 * warning at all. This hook is that missing half.
 *
 * Usage: the host owns the guard, the section reports what it is holding.
 *
 * ```tsx
 * const guard = useUnsavedChangesGuard();
 *
 * <button onClick={() => { if (guard.confirmDiscard()) setSection(key); }}>…</button>
 * <MySection onUnsavedChangesChange={guard.setUnsavedCount} />
 * ```
 */
import { useCallback, useState } from 'react';

/**
 * Copy for the confirm.
 *
 * Deliberately echoes the browser's own unload wording ("Changes you made may
 * not be saved"), so the in-page guard and the route-exit guard read as the
 * same warning about the same work.
 *
 * @param count - staged rows about to be discarded.
 * @returns the confirm message.
 */
export const unsavedChangesPrompt = (count: number): string =>
  `You have ${count} unsaved change${count === 1 ? '' : 's'}. `
  + 'Leave this section? Changes you made may not be saved.';

export interface UnsavedChangesGuard {
  /** Staged rows the open section is currently holding. */
  unsavedCount: number;
  /**
   * Stable setter the open section calls as its staged count moves. Stable
   * identity matters: sections report from an effect, and a changing callback
   * would re-fire it on every render of the host.
   */
  setUnsavedCount: (count: number) => void;
  /**
   * @returns true when it is safe to leave — nothing is staged, or the user
   *   agreed to discard it.
   */
  confirmDiscard: () => boolean;
}

/**
 * Track one section's unsaved work and ask before it is discarded.
 *
 * @returns the count, the setter a section reports through, and the guard the
 *   host calls before switching section.
 */
export function useUnsavedChangesGuard(): UnsavedChangesGuard {
  const [unsavedCount, setCount] = useState(0);

  const setUnsavedCount = useCallback((count: number) => {
    setCount(count);
  }, []);

  const confirmDiscard = useCallback((): boolean => {
    if (unsavedCount <= 0) return true;
    return window.confirm(unsavedChangesPrompt(unsavedCount));
  }, [unsavedCount]);

  return { unsavedCount, setUnsavedCount, confirmDiscard };
}
